import { useEffect, useState, useRef, useCallback, useMemo } from 'react'
import { RefreshCw, Clock, Filter } from 'lucide-react'
import type { RadarBubble, RadarZones, RadarRay, RadarConfigResponse, RadarFilterState } from '@/services/api/teacher'
import { DEFAULT_MAX_TIME_RANGE_DAYS, DEFAULT_TIME_RANGE_DAYS } from '@/utils/radarStorage'

// New Liquid Glass Components
import { GlassContainer } from './glass-layers/GlassContainer'
import { GlassZones } from './glass-layers/GlassZones'
import { OrganicRays } from './svg/OrganicRays'
import { RadarCanvas } from './canvas/RadarCanvas'
import { HoveredBubbleInfo } from './ui/HoveredBubbleInfo'
import { FloatingTooltip } from './ui/FloatingTooltip'
import { RequestSelectionDialog } from './ui/RequestSelectionDialog'
import { RadarFilterDrawer } from './ui/RadarFilterDrawer'
import { RadarErrorBoundary } from './ErrorBoundary'
import { filterBubbles, countActiveFilters, filterRays, withoutDirectorSpecials, stripDirectorGate } from './utils/filterBubbles'

// Hooks
import { useRadarSize, useTouchDevice } from './hooks/useRadarSize'
import { useRadarFocus } from './hooks/useRadarFocus'
import { useInView } from './hooks/useInView'

// Import CSS tokens
import './styles/radar-tokens.css'

// i18n
import {
  getTranslations,
  detectLocale,
  formatDays,
  type Locale,
  radarI18nDe,
} from './i18n'

interface TeacherRadarProps {
  bubbles: RadarBubble[]
  outgoingBubbles?: RadarBubble[]
  incomingBubbles?: RadarBubble[]
  zones: RadarZones | undefined
  rays: RadarRay[]
  timeRange: { days: number; from: string; to: string } | undefined
  config: RadarConfigResponse | null
  isLoading: boolean
  isConfigLoading: boolean
  onBubbleClick: (bubble: RadarBubble) => void
  onTimeRangeChange: (days: number) => void
  onRefresh: () => void
  locale?: Locale
  // Filter (filter-fix-v2) — optional for backward compat / tests
  filterState?: RadarFilterState
  onFilterChange?: (partial: Partial<RadarFilterState>) => void
  onResetFilter?: () => void
  isFilterLocked?: boolean
  filterDefaultState?: RadarFilterState
  directorEnabled?: boolean
}

/**
 * Teacher Radar - Liquid Glass Edition v2
 *
 * Architecture:
 * - Layer -2: Glass Zones (DOM, backdrop-filter)
 * - Layer -1: Organic Rays (SVG)
 * - Layer 0: Canvas (transparent bg, bubbles + center)
 * - Layer +1: UI overlays (glass panels)
 *
 * Phase 2: Accessibility & Performance
 * - Keyboard navigation (Tab, Arrows, Enter, Escape)
 * - Screen reader support
 * - Intersection Observer for performance
 * - ARIA attributes
 */
// Error fallback component
function RadarErrorFallback({ message }: { message: string }) {
  return (
    <div className="p-6 text-center rounded-xl border border-red-200 bg-red-50">
      <p className="text-red-600">{message}</p>
    </div>
  )
}

export function TeacherRadar({
  bubbles,
  outgoingBubbles: outgoingBubblesProp,
  incomingBubbles: incomingBubblesProp,
  zones,
  rays,
  timeRange,
  config,
  isLoading,
  isConfigLoading,
  onBubbleClick,
  onTimeRangeChange,
  onRefresh,
  locale: localeProp,
  filterState,
  onFilterChange,
  onResetFilter,
  isFilterLocked,
  filterDefaultState,
  directorEnabled = false,
}: TeacherRadarProps) {
  // Get locale and translations
  const locale = localeProp ?? detectLocale()
  const t = getTranslations(locale)

  // Validate inputs — flag only; the actual fallback render happens AFTER all hooks
  // (rules-of-hooks: no early return before hooks). filter-fix-v3 B3.
  const hasInvalidBubbles = !Array.isArray(bubbles)
  const safeBubbles = hasInvalidBubbles ? [] : bubbles
  if (hasInvalidBubbles) {
    console.error('TeacherRadar: bubbles must be an array')
  }

  // Validate bubble IDs are unique
  const ids = safeBubbles.map(b => b.id)
  const duplicates = ids.filter((id, i) => ids.indexOf(id) !== i)
  if (duplicates.length > 0) {
    console.error('TeacherRadar: Duplicate bubble IDs:', duplicates)
  }

  // Filter out invalid bubbles (don't just log, actually filter)
  const validBubbles = safeBubbles.filter(b => {
    const isValid = b.id && b.request_id && typeof b.angle_degrees === 'number'
    if (!isValid) {
      console.warn('TeacherRadar: Filtering invalid bubble:', b)
    }
    return isValid
  })

  const outgoingBubblesRaw = outgoingBubblesProp ?? validBubbles.filter(b => b.bubble_type !== 'incoming')
  const incomingBubblesRaw = incomingBubblesProp ?? validBubbles.filter(b => b.bubble_type === 'incoming')
  const outgoingBubbles = directorEnabled ? outgoingBubblesRaw : stripDirectorGate(outgoingBubblesRaw)
  const incomingBubbles = directorEnabled ? incomingBubblesRaw : stripDirectorGate(incomingBubblesRaw)
  const displayDays = timeRange?.days ?? config?.default_time_range_days ?? DEFAULT_TIME_RANGE_DAYS

  // Client-side filtering (filter-fix-v2)
  const applyTypeFilter = !!config?.show_incoming_in_radar
  const effectiveFilter = useMemo(() => {
    if (!filterState) return filterState
    if (directorEnabled) return filterState
    return withoutDirectorSpecials(filterState)
  }, [filterState, directorEnabled])
  const filteredOutgoing = useMemo(
    () => (effectiveFilter ? filterBubbles(outgoingBubbles, effectiveFilter, { applyTypeFilter }) : outgoingBubbles),
    [outgoingBubbles, effectiveFilter, applyTypeFilter],
  )
  const filteredIncoming = useMemo(
    () => (effectiveFilter ? filterBubbles(incomingBubbles, effectiveFilter, { applyTypeFilter }) : incomingBubbles),
    [incomingBubbles, effectiveFilter, applyTypeFilter],
  )
  const allBubblesForRender = useMemo(
    () => [...filteredOutgoing, ...filteredIncoming],
    [filteredOutgoing, filteredIncoming],
  )
  const isFiltering = !!filterState && allBubblesForRender.length !== (outgoingBubbles.length + incomingBubbles.length)
  // Filter circumference rays to match visible bubbles — ONLY when filtering is
  // active, so the unfiltered view is byte-identical to before (filter-fix-v9).
  const filteredRays = isFiltering ? filterRays(rays, allBubblesForRender) : rays
  const activeFilterCount = filterState && filterDefaultState
    ? countActiveFilters(effectiveFilter ?? filterState, filterDefaultState)
    : 0

  // Use validBubbles throughout
  const allBubbles = validBubbles

  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false)

  const [localTimeRange, setLocalTimeRange] = useState(displayDays)
  const [hoveredBubble, setHoveredBubble] = useState<RadarBubble | null>(null)

  // Mobile: Track tapped bubble separately
  const [tappedBubble, setTappedBubble] = useState<RadarBubble | null>(null)

  // Multi-request selection dialog
  const [selectedBubble, setSelectedBubble] = useState<RadarBubble | null>(null)

  // Announcement for screen readers
  const [announcement, setAnnouncement] = useState('')

  // Responsive sizing
  const { size: radarSize, isMobile } = useRadarSize()
  const isTouchDevice = useTouchDevice()

  // Tooltip state with mouse position
  const [tooltipState, setTooltipState] = useState<{
    visible: boolean
    position: { x: number; y: number }
  }>({
    visible: false,
    position: { x: 0, y: 0 },
  })

  // Refs
  const radarContainerRef = useRef<HTMLDivElement>(null)
  const radarSectionRef = useRef<HTMLElement>(null)

  // Check if radar is in view (for performance)
  const isInView = useInView(radarSectionRef, { threshold: 0.1 })

  // Keyboard navigation
  const handleBubbleSelect = useCallback((bubble: RadarBubble | null) => {
    setHoveredBubble(bubble)
    if (bubble) {
      setAnnouncement(`${bubble.subject_name} am ${bubble.lesson_date}`)
    }
  }, [])

  const handleBubbleClick = useCallback((bubble: RadarBubble) => {
    // On mobile/touch: track tapped bubble for info display
    if (isMobile || isTouchDevice) {
      setTappedBubble(bubble)
    }
    
    // Check if bubble has multiple requests (aggregated bubble)
    if (bubble.request_ids && bubble.request_ids.length > 1) {
      setSelectedBubble(bubble)
    } else {
      onBubbleClick(bubble)
    }
  }, [isMobile, isTouchDevice, onBubbleClick])

  const handleBubbleActivate = useCallback((bubble: RadarBubble) => {
    // Aggregierte Bubbles öffnen den RequestSelectionDialog (kein Confirm nötig)
    if (bubble.request_ids && bubble.request_ids.length > 1) {
      handleBubbleClick(bubble)
      return
    }

    // Keyboard-Aktivierung: Bestätigung anzeigen, um Desorientierung zu vermeiden
    const targetLabel = bubble.bubble_type === 'incoming' ? 'Anfragen an mich' : 'Anfragen von mir'
    const confirmed = window.confirm(`Zur Seite "${targetLabel}" navigieren?`)
    if (confirmed) {
      handleBubbleClick(bubble)
    }
  }, [handleBubbleClick])

  const {
    focusedId,
    setFocusedId,
    focusedBubble,
    handleKeyDown,
  } = useRadarFocus({
    bubbles: allBubblesForRender,
    onBubbleSelect: handleBubbleSelect,
    onBubbleActivate: handleBubbleActivate,
  })

  // Sync focused bubble with hovered bubble
  useEffect(() => {
    if (focusedBubble) {
      setHoveredBubble(focusedBubble)
    }
  }, [focusedBubble])

  const visibleIdKey = allBubblesForRender.map(b => b.id).join('|')
  useEffect(() => {
    const visible = new Set(visibleIdKey ? visibleIdKey.split('|') : [])
    setHoveredBubble(prev => {
      if (prev && !visible.has(prev.id)) {
        setTooltipState(t => ({ ...t, visible: false }))
        return null
      }
      return prev
    })
    setTappedBubble(prev => (prev && !visible.has(prev.id) ? null : prev))
  }, [visibleIdKey])

  useEffect(() => {
    setLocalTimeRange(displayDays)
  }, [displayDays])

  const handleSliderChange = (days: number) => {
    const min = config?.min_time_range_days || 1
    const max = config?.max_time_range_days || DEFAULT_MAX_TIME_RANGE_DAYS
    const clamped = Math.max(min, Math.min(max, days))
    setLocalTimeRange(clamped)
    onTimeRangeChange(clamped)
  }

  const handleRequestSelect = useCallback((requestId: string, lessonRequestIds: string[]) => {
    if (selectedBubble) {
      onBubbleClick({ ...selectedBubble, request_id: requestId, lesson_request_id: lessonRequestIds[0] || '', lesson_request_ids: lessonRequestIds })
      setSelectedBubble(null)
    }
  }, [selectedBubble, onBubbleClick])

  const handleBubbleHover = useCallback((bubble: RadarBubble | null, mousePos?: { x: number; y: number }) => {
    setHoveredBubble(bubble)
    setFocusedId(bubble?.id || null)

    if (bubble && mousePos && !isMobile) {
      setTooltipState({
        visible: true,
        position: mousePos,
      })
    } else {
      setTooltipState(prev => ({ ...prev, visible: false }))
    }
  }, [isMobile, setFocusedId])

  // Default zones if not provided
  const defaultZones: RadarZones = {
    panic_zone: { threshold_hours: 2, type: 'fixed', color: '#ef4444', opacity: 0.2, label: 'PANIKZONE' },
    middle_zone: { threshold_hours: 48, type: 'fixed', color: '#f97316', opacity: 0.15, label: 'MITTELZONE' },
    outer_zone: { threshold_hours: null, type: 'auto', color: '#94a3b8', opacity: 0.1, label: 'AUßENZONE' },
  }

  const activeZones = zones || defaultZones

  // Wrap handleKeyDown to convert React event to native event
  const handleSectionKeyDown = useCallback((e: React.KeyboardEvent<HTMLElement>) => {
    handleKeyDown(e.nativeEvent)
  }, [handleKeyDown])

  // Late fallback render (rules-of-hooks: kept after all hooks). filter-fix-v3 B3.
  if (hasInvalidBubbles) {
    return <RadarErrorFallback message={t.errors.unknown} />
  }

  return (
    <section
      ref={radarSectionRef}
      aria-label={t.aria.regionLabel}
      role="region"
      onKeyDown={handleSectionKeyDown}
    >
      {/* Screen reader instructions */}
      <div id="radar-instructions" className="sr-only">
        {t.aria.instructions}
      </div>

      {/* Live region for announcements */}
      <div role="status" aria-live="polite" className="sr-only">
        {announcement}
      </div>

      <GlassContainer className="w-full relative">
        {/* Header */}
        <div className="p-4 border-b border-gray-200/50 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-gray-800">{t.title}</h3>
            {timeRange && (
              <span className="text-xs text-gray-500">
                ({formatDays(timeRange.days, locale)})
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {allBubbles.length > 0 && (
              <span
                className="text-xs px-2 py-1 rounded-lg glass-chip"
              >
                {isFiltering
                  ? t.filter.countRequests(allBubblesForRender.length, outgoingBubbles.length + incomingBubbles.length)
                  : `${outgoingBubbles.length} ${t.bubbleTypes.outgoing}${incomingBubbles.length > 0 ? ` · ${incomingBubbles.length} ${t.bubbleTypes.incoming}` : ''}`}
              </span>
            )}
            {filterState && onFilterChange && !isFilterLocked && (
              <button
                onClick={() => setFilterDrawerOpen(v => !v)}
                className={`relative p-2 rounded-lg hover:bg-white/50 transition border ${filterDrawerOpen ? 'bg-blue-50 border-blue-300' : 'border-gray-200/50'}`}
                title={t.filter.title}
                aria-label={t.filter.title}
              >
                <Filter className={`w-4 h-4 ${activeFilterCount > 0 ? 'text-blue-600' : 'text-gray-600'}`} />
                {activeFilterCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[15px] h-[15px] px-1 rounded-full bg-blue-500 text-white text-[9px] font-bold flex items-center justify-center">
                    {activeFilterCount}
                  </span>
                )}
              </button>
            )}
            <button
              onClick={onRefresh}
              disabled={isLoading}
              className="p-2 rounded-lg hover:bg-white/50 transition disabled:opacity-50"
              title={t.buttons.refresh}
              aria-label={t.aria.refreshButton}
            >
              <RefreshCw className={`w-4 h-4 text-gray-600 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Time Range Selector */}
        <div className="px-4 py-3 border-b border-gray-200/50 flex items-center gap-3">
          <Clock className="w-4 h-4 text-gray-400" />
          <span className="text-xs text-gray-500">{t.form.timeRange}:</span>
          <input
            type="range"
            min={config?.min_time_range_days || 1}
            max={config?.max_time_range_days || DEFAULT_MAX_TIME_RANGE_DAYS}
            value={localTimeRange}
            onChange={(e) => handleSliderChange(Number(e.target.value))}
            disabled={isConfigLoading || !!isFilterLocked}
            className="flex-1 h-1.5 bg-gray-200 rounded-full appearance-none cursor-pointer accent-blue-500 disabled:opacity-50"
            aria-label={t.aria.timeRangeSlider}
          />
          <span className="text-xs font-medium text-gray-700 w-8 text-right">{localTimeRange}d</span>
        </div>

        {/* Content */}
        <div className="p-4">
          {isLoading ? (
            <LoadingState t={t} />
          ) : (
            <div className="flex flex-col gap-4">
              {/* Radar Visualization - Multi-layer architecture */}
              <div
                ref={radarContainerRef}
                className="relative flex justify-center"
                style={{ width: radarSize, height: radarSize, margin: '0 auto' }}
              >
                {/* Layer -2: Glass Zones */}
                <GlassZones
                  zones={activeZones}
                  width={radarSize}
                  height={radarSize}
                  timeRange={timeRange}
                  locale={locale}
                />

                {/* Layer -1: Organic Rays */}
                <OrganicRays
                  rays={filteredRays}
                  width={radarSize}
                  height={radarSize}
                  hideLabels={config?.show_teacher_labels === false}
                />

                {/* Layer 0: Canvas (bubbles + center) */}
                <RadarErrorBoundary>
                  <RadarCanvas
                    bubbles={allBubblesForRender}
                    zones={activeZones}
                    rays={filteredRays}
                    config={config ? {
                      min_bubble_size: config.min_bubble_size,
                      max_bubble_size: config.max_bubble_size,
                      bubble_size_scale: config.bubble_size_scale,
                    } : null}
                    timeRange={timeRange}
                    width={radarSize}
                    height={radarSize}
                    onBubbleClick={handleBubbleClick}
                    onBubbleHover={handleBubbleHover}
                    containerRef={radarContainerRef}
                    isPaused={!isInView}
                    focusedId={focusedId}
                  />
                </RadarErrorBoundary>

                {/* 
                  Center "NOW" Label - pointer-events-none
                  Clicks on center hit the bubble at 0h (Canvas z-index: 2)
                */}
                <CenterNowLabel radarSize={radarSize} t={t} />
              </div>

              {allBubbles.length === 0 && <EmptyState t={t} />}
              {allBubbles.length > 0 && allBubblesForRender.length === 0 && (
                <EmptyState t={t} customText={t.filter.emptyFiltered} />
              )}

              {/* Mobile: Show tapped bubble info */}
              {(isMobile || isTouchDevice) && tappedBubble && (
                <HoveredBubbleInfo bubble={tappedBubble} />
              )}

              {/* Desktop: Show hovered bubble info */}
              {!(isMobile || isTouchDevice) && hoveredBubble && (
                <HoveredBubbleInfo bubble={hoveredBubble} />
              )}
            </div>
          )}

          {/* Legend — frost-v3 (F2): gefilterte Outgoing-Bubbles, damit die
              bedingte Frost-Legende nur erscheint, wenn Frost auch sichtbar
              ist (Konsistenz mit AdminRadar/filteredBubbles). */}
          <Legend
            outgoingBubbles={filteredOutgoing}
            incomingBubbles={filteredIncoming}
            zones={activeZones}
            t={t}
          />
        </div>

        {/* Floating Tooltip - only on desktop */}
        {!isMobile && !isTouchDevice && (
          <FloatingTooltip
            bubble={hoveredBubble}
            position={tooltipState.position}
            visible={tooltipState.visible}
          />
        )}

        {/* Multi-request selection dialog */}
        <RequestSelectionDialog
          isOpen={!!selectedBubble}
          onClose={() => setSelectedBubble(null)}
          requestIds={selectedBubble?.request_ids || []}
          onSelect={handleRequestSelect}
          bubble={selectedBubble}
        />
      </GlassContainer>

      {/* Filter Drawer (filter-fix-v2/v3) — rendered OUTSIDE GlassContainer to
          avoid overflow-hidden clipping. Fixed-positioned (viewport-relative). */}
      {filterState && onFilterChange && !isFilterLocked && (
        <RadarFilterDrawer
          open={filterDrawerOpen}
          onClose={() => setFilterDrawerOpen(false)}
          filterState={filterState}
          setFilter={onFilterChange}
          resetFilter={onResetFilter ?? (() => {})}
          isLocked={!!isFilterLocked}
          radarType="teacher"
          showIncoming={!!config?.show_incoming_in_radar}
          allBubbles={allBubbles}
          totalCount={outgoingBubbles.length + incomingBubbles.length}
          visibleCount={allBubblesForRender.length}
          t={t}
          directorEnabled={directorEnabled}
        />
      )}
    </section>
  )
}

/**
 * Center "NOW" Label - Uses CSS class instead of inline CSS vars
 */
function CenterNowLabel({ radarSize, t }: { radarSize: number; t: typeof radarI18nDe }) {
  const centerPos = radarSize / 2

  return (
    <div
      className="absolute pointer-events-none"
      style={{
        left: centerPos,
        top: centerPos,
        transform: 'translate(-50%, -50%)',
        zIndex: 50,
      }}
    >
      <div
        className="px-3 py-1.5 rounded-full text-xs font-bold tracking-wide glass-chip"
        style={{
          color: '#1f2937',
          boxShadow: '0 0 12px rgba(59, 130, 246, 0.25)',
          background: 'rgba(255, 255, 255, 0.85)',
        }}
      >
        {t.nowLabel}
      </div>
    </div>
  )
}

/**
 * Loading State
 */
function LoadingState({ t }: { t: typeof radarI18nDe }) {
  return (
    <div className="text-center py-12" role="status" aria-live="polite">
      <div
        className="inline-block rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent animate-spin"
        style={{
          boxShadow: '0 0 20px rgba(59, 130, 246, 0.3)',
        }}
      />
      <p className="mt-3 text-sm text-gray-500">{t.loading.default}</p>
    </div>
  )
}

/**
 * Empty State
 */
function EmptyState({ t, customText }: { t: typeof radarI18nDe; customText?: string }) {
  return (
    <div
      className="text-center py-8 text-gray-500 text-sm rounded-xl border"
      style={{
        background: 'rgba(255,255,255,0.3)',
        borderColor: 'rgba(255,255,255,0.5)',
      }}
      role="status"
    >
      {customText ?? t.empty.default}
    </div>
  )
}

/**
 * Legend Component
 */
interface LegendProps {
  outgoingBubbles: RadarBubble[]
  incomingBubbles: RadarBubble[]
  zones: RadarZones
  t: typeof radarI18nDe
}

function Legend({ outgoingBubbles, incomingBubbles, zones, t }: LegendProps) {
  // frost-v2 (E12): Frost-Legende nur, wenn tatsächlich eine gepauste
  // Outgoing-Anfrage sichtbar ist.
  const hasGateFrozen = outgoingBubbles.some(b => b.gate_pending)
  return (
    <div className="mt-4 pt-3 border-t border-gray-200/50">
      <div className="flex flex-col gap-2 text-xs text-gray-600">
        {outgoingBubbles.length > 0 && (
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-medium text-gray-500">{t.legend.outgoingRequests}:</span>
            <LegendItem color="#f97316" label={t.status.open} />
            <LegendItem color="#3b82f6" label={t.filter.statusInterested} />
            <LegendItem color="#22c55e" label={t.status.accepted} />
            <LegendItem color="#ef4444" label={t.status.declined} />
            <LegendItem color="#6b7280" label={t.filter.statusCancelled} />
            <LegendItem color="#6b7280" label={t.filter.statusExpired} />
            {hasGateFrozen && (
              <LegendItem color="#7dd3fc" bgColor="#e0f2fe" label={t.legend.gateFrozen} ring />
            )}
          </div>
        )}

        {incomingBubbles.length > 0 && (
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-medium text-gray-500">{t.legend.incomingRequests}:</span>
            <LegendItem color="#f97316" label={t.status.open} bordered />
            <LegendItem color="#3b82f6" label={t.filter.statusInterested} bordered />
            <LegendItem color="#22c55e" label={t.status.accepted} bordered />
            <LegendItem color="#ef4444" label={t.status.declined} bordered />
            <LegendItem color="#6b7280" label={t.filter.statusCancelled} bordered />
            <LegendItem color="#6b7280" label={t.filter.statusExpired} bordered />
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <LegendItem color="#ef4444" bgColor="rgba(239, 68, 68, 0.1)" label={`${t.legend.panicZone} (≤${zones.panic_zone.threshold_hours ?? 2}${t.legend.hoursShort})`} />
          <LegendItem color="#f97316" bgColor="rgba(249, 115, 22, 0.1)" label={`${t.legend.middleZone} (${zones.panic_zone.threshold_hours ?? 2}-${zones.middle_zone.threshold_hours ?? 48}${t.legend.hoursShort})`} />
        </div>
      </div>
    </div>
  )
}

/**
 * Legend Item
 */
interface LegendItemProps {
  color: string
  label: string
  bordered?: boolean
  bgColor?: string
  ring?: boolean
}

function LegendItem({ color, label, bordered, bgColor, ring }: LegendItemProps) {
  return (
    <div className="flex items-center gap-1">
      <span
        className="w-3 h-3 rounded-full"
        style={{
          background: bordered ? 'white' : bgColor || color,
          border: bordered || ring ? `2px solid ${color}` : bgColor ? `1px solid ${color}` : 'none',
        }}
      />
      <span>{label}</span>
    </div>
  )
}
